"use client";

import { CalendarDays, MapPin, X } from "lucide-react";
import type { FeedPostContextTag } from "@/actions/post";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { Button } from "@/components/ui/button";
import { formatEventDate } from "@/lib/dates/event-display";
import { cn } from "@/lib/utils";

type PostEventTagProps = {
  tag: FeedPostContextTag;
  compact?: boolean;
  onRemove?: () => void;
  /**
   * Link the chip to the event/venue page (feed cards). The composer tray
   * omits it so taps don't navigate away from a draft.
   */
  asLink?: boolean;
};

/**
 * Real-world context chip on post cards: the event (or venue) a post is
 * tagged to. Mirrors PostLocationTag's shape so the two chips can sit
 * side by side in the same tray/row.
 */
export function PostEventTag({
  tag,
  compact = false,
  onRemove,
  asLink = false,
}: PostEventTagProps) {
  const isEvent = tag.type === "event_occurrence";
  const Icon = isEvent ? CalendarDays : MapPin;
  const name = isEvent ? tag.eventName : tag.venueName;
  const href = isEvent
    ? `/hiives/${tag.hiiveSlug}/events/${tag.eventSlug}`
    : `/hiives/${tag.hiiveSlug}/venues/${tag.venueSlug}`;
  const dateLabel =
    isEvent && !compact
      ? formatEventDate(new Date(tag.startTime), "EEE, d MMM")
      : null;

  // Feed cards use a lightweight pill (mirrors PostLocationTag's compact
  // variant); the full row is reserved for composer trays.
  if (compact) {
    const pillBody = (
      <>
        <Icon className="h-3.5 w-3.5 shrink-0 text-brand" />
        <span className="truncate text-xs font-medium text-text-primary">
          {name}
        </span>
      </>
    );

    return (
      <div
        className={cn(
          "flex max-w-full min-w-0 items-center gap-1.5 rounded-full border border-border bg-surface-alt/60 py-1 pl-2.5",
          onRemove ? "pr-1" : "pr-2.5",
        )}
      >
        {asLink ? (
          <BackAwareLink
            href={href}
            onClick={(event) => event.stopPropagation()}
            className="flex min-w-0 items-center gap-1.5"
            aria-label={`Open ${name} ${isEvent ? "event" : "venue"} page`}
          >
            {pillBody}
          </BackAwareLink>
        ) : (
          <span className="flex min-w-0 items-center gap-1.5">{pillBody}</span>
        )}
        {onRemove ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-5 w-5 shrink-0 rounded-full text-text-secondary"
            onClick={onRemove}
            aria-label={isEvent ? "Remove event tag" : "Remove venue tag"}
          >
            <X className="h-3 w-3" />
          </Button>
        ) : null}
      </div>
    );
  }

  const body = (
    <>
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand">
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-text-primary">
          {name}
        </span>
        {dateLabel ? (
          <span className="mt-0.5 block truncate text-xs text-text-secondary">
            {dateLabel}
          </span>
        ) : null}
      </span>
    </>
  );

  return (
    <div className="flex items-start gap-2 rounded-xl border border-border bg-surface-alt px-3.5 py-3 text-left">
      {asLink ? (
        <BackAwareLink
          href={href}
          onClick={(event) => event.stopPropagation()}
          className="flex min-w-0 flex-1 items-start gap-2"
          aria-label={`Open ${name} ${isEvent ? "event" : "venue"} page`}
        >
          {body}
        </BackAwareLink>
      ) : (
        <div className="flex min-w-0 flex-1 items-start gap-2">{body}</div>
      )}
      {onRemove ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 rounded-full text-text-secondary"
          onClick={onRemove}
          aria-label={isEvent ? "Remove event tag" : "Remove venue tag"}
        >
          <X className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  );
}
